import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    TextField,
    InputLabel,
    Select,
    MenuItem,
    FormControl
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const subSpecialtySchema = Yup.object().shape({
    name: Yup.string()
        .min(2, "Too Short!")
        .max(50, "Too Long!")
        .required("Required"),
    duration: Yup.number().required("Required"),
    specialtyId: Yup.number().required("Required"),
});

const SubSpecialtyModal = ({ open, onClose, subSpecialtyId, reload }) => {
    const [subSpecialty, setSubSpecialty] = useState({
        name: "",
        duration: "",
        price: "",
        specialtyId: ""
    });
    const [specialties, setSpecialties] = useState([]);

    useEffect(() => {
        const fetchSpecialties = async () => {
            try {
                const { data } = await api.get("/specialties");
                setSpecialties(data);
            } catch (err) {
                toastError(err);
            }
        };
        fetchSpecialties();
    }, [open]);

    useEffect(() => {
        const fetchSubSpecialty = async () => {
            if (!subSpecialtyId) return;
            try {
                const { data } = await api.get(`/subspecialties/${subSpecialtyId}`);
                setSubSpecialty(data);
            } catch (err) {
                toastError(err);
            }
        };

        fetchSubSpecialty();
    }, [subSpecialtyId, open]);

    const handleClose = () => {
        onClose();
        setSubSpecialty({ name: "", duration: "", price: "", specialtyId: "" });
    };

    const handleSaveSubSpecialty = async (values) => {
        try {
            if (subSpecialtyId) {
                await api.put(`/subspecialties/${subSpecialtyId}`, values);
            } else {
                await api.post("/subspecialties", values);
            }
            toast.success(i18n.t("subSpecialties.toasts.success"));
            reload();
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                {subSpecialtyId
                    ? i18n.t("subSpecialties.modal.edit")
                    : i18n.t("subSpecialties.modal.add")}
            </DialogTitle>
            <Formik
                initialValues={subSpecialty}
                enableReinitialize={true}
                validationSchema={subSpecialtySchema}
                onSubmit={(values, actions) => {
                    setTimeout(() => {
                        handleSaveSubSpecialty(values);
                        actions.setSubmitting(false);
                    }, 400);
                }}
            >
                {({ touched, errors, isSubmitting, isValid }) => (
                    <Form>
                        <DialogContent dividers>
                            <Field
                                as={TextField}
                                label={i18n.t("subSpecialties.modal.form.name")}
                                autoFocus
                                name="name"
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                            />
                            <Field
                                as={TextField}
                                label={i18n.t("subSpecialties.modal.form.duration")}
                                name="duration"
                                type="number"
                                error={touched.duration && Boolean(errors.duration)}
                                helperText={touched.duration && errors.duration}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                            />
                            <Field
                                as={TextField}
                                label={i18n.t("subSpecialties.modal.form.price")}
                                name="price"
                                type="number"
                                variant="outlined"
                                margin="dense"
                                fullWidth
                            />
                            <FormControl fullWidth margin="dense" variant="outlined">
                                <InputLabel>{i18n.t("subSpecialties.modal.form.specialty")}</InputLabel>
                                <Field
                                    as={Select}
                                    label={i18n.t("subSpecialties.modal.form.specialty")}
                                    name="specialtyId"
                                    error={touched.specialtyId && Boolean(errors.specialtyId)}
                                >
                                    {specialties.map((spec) => (
                                        <MenuItem key={spec.id} value={spec.id}>
                                            {spec.name}
                                        </MenuItem>
                                    ))}
                                </Field>
                            </FormControl>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={handleClose}
                                color="secondary"
                                disabled={isSubmitting}
                                variant="outlined"
                            >
                                {i18n.t("global.buttons.cancel")}
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                disabled={isSubmitting || !isValid}
                                variant="contained"
                                className={isSubmitting ? "circularProgress" : ""}
                            >
                                {i18n.t("global.buttons.add")}
                                {isSubmitting && (
                                    <CircularProgress
                                        size={24}
                                        className="buttonProgress"
                                    />
                                )}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default SubSpecialtyModal;
